/* ── Headshot Selector Modal ──────────────────────────────────────────────
   Lets the signed-in player pick a profile photo from their saved photos,
   falling back to browsing recent league photos if they have none saved.
   Mutates public.profiles.avatar_url for the CURRENT session user only —
   userId must always come from the authenticated session, never a prop
   sourced from a route param or another user's data. Saves full_url + 
   avatar_crop JSONB. Uses CSS transforms (no canvas).
──────────────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  X,
  Image as ImageIcon,
  Loader2,
  RotateCcw,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import CroppedAvatar from "./CroppedAvatar";

const HeadshotSelectorModal = ({
  isOpen,
  onClose,
  userId,
  onAvatarUpdated,
}) => {
  const [savedPhotos, setSavedPhotos] = useState([]);
  const [leaguePhotos, setLeaguePhotos] = useState([]);
  const [mode, setMode] = useState("saved"); // "saved" | "browse" | "crop"
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [crop, setCrop] = useState({ zoom: 1, offsetX: 0, offsetY: 0 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const viewportRef = useRef(null);
  const imgRef = useRef(null);
  const isDragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const loadSaved = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("saved_photos")
      .select("photo:photos(id, thumbnail_url, full_url)")
      .eq("profile_id", userId)
      .order("created_at", { ascending: false });

    if (err) {
      setError("Couldn't load your saved photos.");
    } else {
      const photos = (data || []).map((r) => r.photo).filter(Boolean);
      setSavedPhotos(photos);
      // No bookmarks yet? Jump straight to the browse fallback.
      if (photos.length === 0) setMode("browse");
    }
    setLoading(false);
  }, [userId]);

  const loadLeaguePhotos = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("photos")
      .select("id, thumbnail_url, full_url")
      .order("id", { ascending: false })
      .limit(60);

    if (err) setError("Couldn't load league photos.");
    setLeaguePhotos(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!isOpen || !userId) return;
    setMode("saved");
    setError(null);
    setSelectedPhoto(null);
    loadSaved();
  }, [isOpen, userId, loadSaved]);

  useEffect(() => {
    if (isOpen && mode === "browse" && leaguePhotos.length === 0) {
      loadLeaguePhotos();
    }
  }, [isOpen, mode, leaguePhotos.length, loadLeaguePhotos]);

  const startCrop = (photo) => {
    setSelectedPhoto(photo);
    setCrop({ zoom: 1, offsetX: 0, offsetY: 0 });
    setMode("crop");
  };

  // Pan handlers
  const handlePointerDown = (e) => {
    e.preventDefault();
    viewportRef.current?.setPointerCapture(e.pointerId);
    isDragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handlePointerMove = (e) => {
    if (!isDragging.current || !imgRef.current) return;
    e.preventDefault();
    const dx = (e.clientX - lastPos.current.x) / 200; // sensitivity
    const dy = (e.clientY - lastPos.current.y) / 200;
    setCrop((prev) => ({
      ...prev,
      offsetX: Math.max(-1, Math.min(1, prev.offsetX + dx)),
      offsetY: Math.max(-1, Math.min(1, prev.offsetY + dy)),
    }));
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handlePointerUp = (e) => {
    isDragging.current = false;
    try {
      viewportRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* already released — safe to ignore */
    }
  };

  // Zoom
  const adjustZoom = (delta) => {
    setCrop((prev) => ({
      ...prev,
      zoom: Math.max(0.5, Math.min(4, prev.zoom + delta)),
    }));
  };

  const resetCrop = () => setCrop({ zoom: 1, offsetX: 0, offsetY: 0 });

  const saveCrop = async () => {
    if (!selectedPhoto) return;
    setSaving(true);
    setError(null);

    const { error: updateErr } = await supabase
      .from("profiles")
      .update({
        avatar_url: selectedPhoto.full_url,
        avatar_crop: crop,
      })
      .eq("id", userId);

    setSaving(false);

    if (updateErr) {
      setError("Failed to save headshot.");
      return;
    }

    onAvatarUpdated(selectedPhoto.full_url, crop);
    onClose();
  };

  const photosToShow = mode === "saved" ? savedPhotos : leaguePhotos;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white dark:bg-gray-900 rounded-3xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b dark:border-gray-800">
              <h3 className="text-xl font-semibold dark:text-white">
                {mode === "crop" ? "Crop Headshot" : "Choose Your Headshot"}
              </h3>
              <button onClick={onClose} className="...">
                <X size={18} className="dark:text-white" />
              </button>
            </div>

            {/* Mode tabs (saved/browse) */}
            {mode !== "crop" && savedPhotos.length > 0 && (
              <div className="flex gap-2 px-6 pt-4">
                {" "}
                <div className="flex gap-2 px-6 pt-4">
                  <button
                    onClick={() => setMode("saved")}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                      mode === "saved"
                        ? "bg-orange-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    Your Saved Photos
                  </button>
                  <button
                    onClick={() => setMode("browse")}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                      mode === "browse"
                        ? "bg-orange-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    Browse League Photos
                  </button>
                </div>{" "}
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-6">
              {error && <div className="...">{error}</div>}

              {/* Grid Selection */}
              {mode !== "crop" && (
                <>
                  {loading ? (
                    <div className="grid grid-cols-3 gap-3">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div
                          key={i}
                          className="aspect-square rounded-2xl bg-gray-100 dark:bg-gray-800 animate-pulse"
                        />
                      ))}
                    </div>
                  ) : photosToShow.length === 0 ? (
                    <div className="text-center py-14">
                      <ImageIcon
                        size={32}
                        className="mx-auto mb-3 text-gray-300 dark:text-gray-700"
                      />
                      <p className="text-gray-500 dark:text-gray-400">
                        {mode === "saved"
                          ? "You haven't saved any photos yet."
                          : "No league photos available yet."}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      {photosToShow.map((photo) => (
                        <button
                          key={photo.id}
                          onClick={() => startCrop(photo)}
                          className="relative aspect-square rounded-2xl overflow-hidden hover:border-orange-500 ..."
                        >
                          <img
                            src={photo.thumbnail_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Cropper View */}
              {mode === "crop" && selectedPhoto && (
                <div className="space-y-6">
                  {/* Live Preview */}
                  <div className="flex justify-center">
                    <CroppedAvatar
                      avatarUrl={selectedPhoto.full_url}
                      avatarCrop={crop}
                      size={120}
                    />
                  </div>

                  {/* Crop Viewport */}
                  <div
                    ref={viewportRef}
                    style={{ touchAction: "none" }}
                    className="relative mx-auto w-72 h-72 rounded-full overflow-hidden border-4 border-orange-500 shadow-inner bg-gray-100 dark:bg-gray-800 cursor-move"
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                  >
                    <img
                      ref={imgRef}
                      src={selectedPhoto.full_url}
                      alt="Crop preview"
                      className="absolute inset-0 w-full h-full object-cover select-none"
                      style={{
                        transform: `scale(${crop.zoom}) translate(${crop.offsetX * 100}%, ${crop.offsetY * 100}%)`,
                        transformOrigin: "center center",
                      }}
                      draggable={false}
                    />
                    {/* Optional subtle grid overlay for framing */}
                  </div>

                  {/* Controls */}
                  <div className="flex flex-col items-center gap-4">
                    <div className="flex gap-3">
                      <button
                        onClick={() => adjustZoom(-0.1)}
                        className="p-3 rounded-full bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white"
                      >
                        <ZoomOut size={20} />
                      </button>
                      <button
                        onClick={() => adjustZoom(0.1)}
                        className="p-3 rounded-full bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white"
                      >
                        <ZoomIn size={20} />
                      </button>
                      <button
                        onClick={resetCrop}
                        className="p-3 rounded-full bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white"
                      >
                        <RotateCcw size={20} />
                      </button>
                    </div>

                    <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
                      Drag to pan • Scroll / buttons to zoom
                    </div>

                    <div className="flex gap-3 w-full">
                      <button
                        onClick={() => setMode("saved")}
                        className="flex-1 py-3 rounded-2xl border dark:border-gray-700 dark:text-white flex items-center justify-center gap-2"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveCrop}
                        disabled={saving}
                        className="flex-1 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-2xl disabled:opacity-70 flex items-center justify-center gap-2"
                      >
                        {saving && (
                          <Loader2 className="animate-spin" size={18} />
                        )}
                        Save Headshot
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default HeadshotSelectorModal;
